import { Box, Button, Stack } from "@mui/material"
import { useQueryAdminDashboardBehaviors } from "api/reactQuery/queries/adminDashboard"
import Dialog from "components/common/dialog/dialog"
import { useTranslation } from "react-i18next"
import {
  IDashboardBehaviorSelection,
  IDashboardPeriodParams,
} from "ts/interfaces/AdminDashboard"
import { DashboardBehaviorType } from "ts/types/AdminDashboard"

import { hasMoreThan } from "./behaviorFormat"
import BehaviorList from "./BehaviorList"
import DashboardSectionBody, { RowsSkeleton } from "./DashboardSectionBody"

const MODAL_ROWS = 30
// BED caps `limit` at 20, so the top 30 is two pages.
const FIRST_PAGE = { skip: 0, limit: 20 }
const SECOND_PAGE = { skip: 20, limit: MODAL_ROWS - 20 }

const Top30List = ({
  params,
  type,
  open,
  onSeeMore,
  selection,
  onSelect,
}: {
  params: IDashboardPeriodParams
  type: DashboardBehaviorType
  open: boolean
  onSeeMore: (type: DashboardBehaviorType) => void
  selection: IDashboardBehaviorSelection | null
  onSelect: (behavior: IDashboardBehaviorSelection) => void
}) => {
  const { t } = useTranslation()
  const first = useQueryAdminDashboardBehaviors(params, type, FIRST_PAGE, {
    enabled: open,
  })
  const second = useQueryAdminDashboardBehaviors(params, type, SECOND_PAGE, {
    enabled: open,
  })
  const rows = [
    ...(first.data?.data.objects ?? []),
    ...(second.data?.data.objects ?? []),
  ]
  const total = first.data?.data.all_objects_count

  return (
    <Box minWidth={0}>
      <DashboardSectionBody
        queries={[first, second]}
        skeleton={<RowsSkeleton rows={MODAL_ROWS} />}
      >
        <BehaviorList
          type={type}
          rows={rows}
          selection={selection}
          onSelect={onSelect}
        />
        {hasMoreThan(total, rows.length, MODAL_ROWS) && (
          <Stack alignItems="center">
            <Button variant="text" onClick={() => onSeeMore(type)}>
              {total
                ? t("adminDashboard.seeMoreCount", { count: total })
                : t("adminDashboard.seeMore")}
            </Button>
          </Stack>
        )}
      </DashboardSectionBody>
    </Box>
  )
}

const BehaviorsSeeAllDialog = ({
  params,
  open,
  onClose,
  onSeeMore,
  selection,
  onSelect,
  onExited,
}: {
  params: IDashboardPeriodParams
  open: boolean
  onClose: () => void
  onSeeMore: (type: DashboardBehaviorType) => void
  selection: IDashboardBehaviorSelection | null
  onSelect: (behavior: IDashboardBehaviorSelection) => void
  onExited: () => void
}) => {
  const { t } = useTranslation()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      titleText={t("adminDashboard.topBonusesAndFines")}
      tabletMaxWidth="720px"
      desktopMaxWidth="1040px"
      customActionButtonsSlot={null}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { mobile: "minmax(0, 1fr)", tablet: "1fr 1fr" },
          gap: 3,
          mx: -3,
        }}
      >
        <Top30List
          params={params}
          type="bonus"
          open={open}
          onSeeMore={onSeeMore}
          selection={selection}
          onSelect={onSelect}
        />
        <Top30List
          params={params}
          type="fine"
          open={open}
          onSeeMore={onSeeMore}
          selection={selection}
          onSelect={onSelect}
        />
      </Box>
    </Dialog>
  )
}

export default BehaviorsSeeAllDialog
